import React, { useState, useMemo } from 'react';
import {
  FileClock,
  UserCheck,
  MessageSquare,
  Check,
  DollarSign,
  PackageCheck,
  Sparkles,
  XCircle,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  FileText,
  Send,
  Edit,
  Download,
  Paperclip
} from 'lucide-react';
import { CollabProposal, ProposalStatus, UserRole, EscrowStatus } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/Badge';

interface ProposalProgressBarProps {
  proposal: CollabProposal;
  onAction: (proposalId: string, action: string, payload?: any) => void;
  onFund: (proposal: CollabProposal) => void; 
  onViewContract: (proposal: CollabProposal) => void;
  onNegotiate: (proposal: CollabProposal) => void;
  onSubmitWork: (proposal: CollabProposal) => void;
  onReviewWork: (proposal: CollabProposal) => void;
}

const STAGES = [
  { id: ProposalStatus.DRAFT, name: 'Draft & Negotiate', icon: Edit, description: "The proposal is being drafted. Terms can be negotiated." },
  { id: ProposalStatus.PENDING, name: 'Admin Review', icon: FileClock, description: "The proposal is awaiting admin approval." },
  { id: ProposalStatus.ACTIVE, name: 'Recipient Action', icon: UserCheck, description: "Sent to the recipient for acceptance." },
  { id: ProposalStatus.ACCEPTED, name: 'Accepted & Funded', icon: Check, description: "Accepted. Escrow is being funded automatically." },
  { id: ProposalStatus.FUNDED, name: 'Work in Progress', icon: DollarSign, description: "Escrow is funded. The recipient can now begin and submit the work." },
  { id: ProposalStatus.FULFILLED, name: 'Work Review', icon: PackageCheck, description: "Work submitted. Proposer to review and release payment." },
  { id: ProposalStatus.EXECUTED, name: 'Complete', icon: Sparkles, description: "Payment released, contract complete." },
];

const TERMINAL_STAGES = [ProposalStatus.REJECTED, ProposalStatus.DENIED];

const getEffectiveStage = (proposal: CollabProposal): ProposalStatus => {
    const status = proposal.status as ProposalStatus;
    const escrowStatus = proposal.escrow?.status;

    if (TERMINAL_STAGES.includes(status)) {
        return status;
    }
    
    if (status === ProposalStatus.EXECUTED) {
        return ProposalStatus.EXECUTED;
    }

    if (status === ProposalStatus.FULFILLED || (proposal.submittedWork && proposal.submittedWork.length > 0)) {
        return ProposalStatus.FULFILLED;
    }

    if (status === ProposalStatus.FUNDED) {
        return ProposalStatus.FUNDED;
    }

    if (status === ProposalStatus.ACCEPTED && escrowStatus === EscrowStatus.FUNDED) {
        return ProposalStatus.FUNDED;
    }

    if (status === ProposalStatus.NEGOTIATION) {
        return ProposalStatus.DRAFT;
    }

    return status;
};

const ProposalProgressBar: React.FC<ProposalProgressBarProps> = ({ proposal, onAction, onFund, onViewContract, onNegotiate, onSubmitWork, onReviewWork }) => {
  const { currentUser: user } = useAuth();
  const [isExpanded, setIsExpanded] = useState(true);

  const effectiveStatus = useMemo(() => getEffectiveStage(proposal), [proposal]);
  const currentStageIndex = STAGES.findIndex(s => s.id === effectiveStatus);
  const isTerminal = TERMINAL_STAGES.includes(proposal.status as ProposalStatus);

  const isAdmin = user?.role === UserRole.ADMIN || user?.role === UserRole.SYSTEM_ADMIN_LIVE;
  const isProposer = proposal.proposerId === user?.id;
  const isRecipient = proposal.artistId === user?.id;

  const ActionButton: React.FC<{onClick: () => void, children: React.ReactNode, className?: string, variant?: 'primary' | 'secondary' | 'destructive', disabled?: boolean}> = ({onClick, children, className, variant = 'primary', disabled}) => (
    <button onClick={onClick} disabled={disabled} className={cn(
        'btn-sm',
        {'btn-primary': variant === 'primary'},
        {'btn-secondary': variant === 'secondary'},
        {'btn-destructive': variant === 'destructive'},
        'disabled:opacity-60 disabled:cursor-not-allowed',
        className
    )}>{children}</button>
  );

  const renderActions = () => {
    switch (effectiveStatus) {
      case ProposalStatus.DRAFT:
        const isNegotiating = proposal.status === ProposalStatus.NEGOTIATION;
        if (isProposer) {
          return (
            <div className="flex gap-2">
              <ActionButton onClick={() => onAction(proposal.id, 'submit')}><Send className="w-4 h-4 mr-1"/>Submit for Review</ActionButton>
              <ActionButton onClick={() => onNegotiate(proposal)} variant="secondary"><MessageSquare className="w-4 h-4 mr-1"/>{isNegotiating ? 'Continue Negotiating' : 'Open Chat'}</ActionButton>
            </div>
          );
        }
        if (isRecipient && isNegotiating) {
            return (
              <div className="flex gap-2 items-center">
                <ActionButton onClick={() => onNegotiate(proposal)} variant="secondary"><MessageSquare className="w-4 h-4 mr-1"/>Negotiate / Chat</ActionButton>
                <p className="text-sm text-kala-400">Waiting for proposer to submit updates.</p>
              </div>
            );
        }
        return <p className="text-sm text-kala-400">This is a draft. Only the proposer can submit it for review.</p>;

      case ProposalStatus.PENDING:
        if (isAdmin) {
          return (
            <div className="flex gap-2">
              <ActionButton onClick={() => onAction(proposal.id, 'approve')} className="bg-green-600 hover:bg-green-500"><Check className="w-4 h-4 mr-1"/>Approve</ActionButton>
              <ActionButton onClick={() => onAction(proposal.id, 'deny')} variant="destructive"><XCircle className="w-4 h-4 mr-1"/>Deny</ActionButton>
            </div>
          );
        }
        return <p className="text-sm text-kala-400 italic">Admin review is pending...</p>;
      
      case ProposalStatus.ACTIVE:
        if (isRecipient) {
            return (
                <div className="flex gap-2 animate-pulse">
                    <ActionButton onClick={() => onAction(proposal.id, 'respond', { response: 'ACCEPTED' })} className="bg-green-600 hover:bg-green-500"><Check className="w-4 h-4 mr-1"/>Accept & Start</ActionButton>
                    <ActionButton onClick={() => onNegotiate(proposal)} variant="secondary"><Edit className="w-4 h-4 mr-1"/>Request Changes</ActionButton>
                    <ActionButton onClick={() => onAction(proposal.id, 'respond', { response: 'REJECTED' })} variant="destructive"><XCircle className="w-4 h-4 mr-1"/>Reject</ActionButton>
                </div>
            );
        }
        return <p className="text-sm text-kala-400">Waiting for {proposal.artistName} to respond.</p>;

      case ProposalStatus.ACCEPTED:
        return <p className="text-sm text-kala-400 italic">Proposal accepted. Escrow funding is processed automatically. The status will update shortly.</p>;

      case ProposalStatus.FUNDED:
          if (isRecipient) {
            return <ActionButton onClick={() => onSubmitWork(proposal)}><Send className="w-4 h-4 mr-1"/>Submit Work File</ActionButton>
          }
          return <p className="text-sm text-kala-400 italic">Awaiting work submission from {proposal.artistName}.</p>;
      
      case ProposalStatus.FULFILLED:
        return null; // All actions are now inside the WorkReviewArea component.

      case ProposalStatus.EXECUTED:
          return <p className="text-sm text-green-400 font-bold flex items-center gap-2"><Sparkles className="w-5 h-5"/>This contract is complete!</p>;

      default:
         return <p className="text-sm text-kala-400">{STAGES.find(s => s.id === effectiveStatus)?.description || "No action required."}</p>;
    }
  };

  const WorkReviewArea = () => (
    <div className="mt-6 p-4 bg-kala-900/70 rounded-lg border border-kala-700 space-y-4">
      <h5 className="font-bold text-white flex items-center gap-2"><PackageCheck className="w-5 h-5 text-blue-400"/>Work Review</h5>
      
      <div className="p-3 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
        <p className="text-xs text-yellow-200">
          <b className="flex items-center gap-2"><AlertCircle className="w-4 h-4"/> Note on Disputes:</b> Portal admins will only intervene based on the strict terms of the agreed-upon contract. Please ensure all discussions are clear and solve disagreements amicably before escalating.
        </p>
      </div>
      
      <div className="space-y-2">
        <h6 className="text-sm font-semibold text-kala-300">Submitted Files:</h6>
        {(proposal.submittedWork && proposal.submittedWork.length > 0) ? (
          <ul className="list-none p-0 m-0 space-y-2">
            {proposal.submittedWork.map((work, index) => (
              <li key={index} className="flex items-center justify-between bg-kala-800 p-2 rounded-md">
                <div className="flex items-center gap-2">
                    <Paperclip className="w-4 h-4 text-kala-400"/>
                    <span className="text-sm text-white">{work.workDescription || `Submission ${index + 1}`}</span>
                </div>
                <a href={work.submittedWorkLink} target="_blank" rel="noopener noreferrer" className="btn-secondary-sm inline-flex items-center"><Download className="w-4 h-4 mr-1"/>Download</a>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-kala-500 italic">No files submitted yet.</p>
        )}
      </div>

      <div className="flex justify-end items-center gap-3 border-t border-kala-700 pt-4">
        {isRecipient && <ActionButton onClick={() => onSubmitWork(proposal)} variant="secondary"><Send className="w-4 h-4 mr-1"/>Submit Another File</ActionButton>}
        {isProposer && <ActionButton onClick={() => onReviewWork(proposal)} variant="primary"><PackageCheck className="w-4 h-4 mr-1"/>Review & Approve Work</ActionButton>}
      </div>
    </div>
  );

  if (isTerminal) {
    return (
       <div className="p-4 bg-kala-800/60 rounded-xl border border-red-500/30">
         <div className="flex justify-between items-center">
            <div>
                <h4 className="font-bold text-white">{proposal.eventName}</h4>
                <p className="text-sm text-kala-400">To: {proposal.artistName}</p>
            </div>
            <div className="flex items-center gap-2 text-red-400">
                <XCircle className="w-6 h-6"/>
                <span className="text-lg font-bold">{proposal.status === ProposalStatus.DENIED ? 'Denied by Admin' : 'Rejected by Recipient'}</span>
            </div>
         </div>
       </div>
    );
  }
  
  const currentStageDetails = STAGES.find(s => s.id === effectiveStatus);

  return (
    <div className={cn("bg-kala-800/60 backdrop-blur-sm border rounded-xl p-5 transition-all", isExpanded ? "border-kala-600" : "border-kala-700 hover:border-kala-600")}>
        <div className="flex justify-between items-start cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
            <div>
                 <h4 className="text-lg font-bold text-white">{proposal.eventName}</h4>
                 <p className="text-sm text-kala-400">To: {proposal.artistName}</p>
            </div>
            <div className="flex items-center gap-4 text-right shrink-0">
                 <div>
                    <p className='text-xl font-mono font-bold text-white'>{proposal.budget} {proposal.currency}</p>
                    <p className='text-xs text-kala-400'>{proposal.paymentType}</p>
                 </div>
                 <div>
                    {proposal.isUrgent && <Badge variant="destructive" className="mb-1">URGENT</Badge>}
                    <p className="font-bold text-kala-200">{currentStageDetails?.name || 'Unknown Stage'}</p>
                 </div>
                <button className="text-kala-400 hover:text-white">
                    {isExpanded ? <ChevronUp/> : <ChevronDown/>}
                </button>
            </div>
        </div>

        {isExpanded && (
            <div className="mt-6 animate-in fade-in duration-300">
                <div className="flex items-start">
                    {STAGES.flatMap((stage, index) => {
                        // DEFINITIVE UI FIX: A stage is complete if the proposal is EXECUTED, or if the current stage is past it.
                        const isCompleted = effectiveStatus === ProposalStatus.EXECUTED || currentStageIndex > index;
                        const isCurrent = currentStageIndex === index;
                        const stageNode = (
                            <div key={stage.id} className="flex flex-col items-center gap-2 text-center w-28">
                                <div className={cn("w-11 h-11 rounded-full flex items-center justify-center border-2 transition-all", {
                                    "bg-green-500/20 border-green-500 text-green-400": isCompleted,
                                    "bg-blue-500/20 border-blue-500 text-blue-400 shadow-lg shadow-blue-900/40 animate-pulse": isCurrent && effectiveStatus !== ProposalStatus.EXECUTED,
                                    "bg-kala-800 border-kala-700 text-kala-500": !isCompleted && !isCurrent,
                                })}>
                                    {isCompleted ? <Check className="w-6 h-6" /> : <stage.icon className="w-5 h-5"/>}
                                </div>
                                <p className={cn("text-xs font-semibold leading-tight", {
                                     "text-green-400": isCompleted,
                                     "text-blue-400": isCurrent && effectiveStatus !== ProposalStatus.EXECUTED,
                                     "text-kala-500": !isCompleted && !isCurrent
                                })}>{stage.name}</p>
                            </div>
                        );
                        const connectorNode = index < STAGES.length - 1 ? (
                            <div key={`${stage.id}-connector`} className={cn("flex-1 h-1 mt-5", isCompleted ? "bg-green-600" : "bg-kala-700")}></div>
                        ) : null;
                        return [stageNode, connectorNode];
                    })}
                </div>

                 {effectiveStatus === ProposalStatus.FULFILLED ? (
                    <WorkReviewArea />
                 ) : currentStageDetails && (
                    <div className="mt-6 p-4 bg-kala-900/70 rounded-lg border border-kala-700">
                        <div className="flex justify-between items-center">
                            <div>
                                <h5 className="font-bold text-white flex items-center gap-2">
                                    Next Step: <span className="text-blue-400">{currentStageDetails.name}</span>
                                </h5>
                                <p className="text-sm text-kala-300 mt-1">{currentStageDetails.description}</p>
                            </div>
                            <div className="flex items-center gap-3">
                               {renderActions()}
                            </div>
                        </div>
                         <div className="border-t border-kala-700 mt-4 pt-3 flex justify-end gap-3">
                            <button onClick={() => onNegotiate(proposal)} className="btn-secondary-sm"><MessageSquare className="w-4 h-4 mr-1" />Chat / Negotiate / View History</button>
                        </div>
                    </div>
                )}
            </div>
        )}
    </div>
  )
}

export default ProposalProgressBar;
